import { $enum } from 'ts-enum-util'
import {
  ICodeTypes,
  IShipmentAccessorialNames,
  IShipmentIssueFollowerTypes,
  IShipmentIssueStates,
} from 'types/graphqlTypes'
import * as yup from 'yup'
import { IAutocompleteOption } from 'transfix-ui'

const REQUIRED_MESSAGE = 'This field is required.'
const REQUIRED_MESSAGE_SHORT = 'required.'

export enum IPaymentTypes {
  reimbursement = 'reimbursement',
  relay = 'relay',
  comcheck = 'comcheck',
}

const SubscribersSchema = yup.object<Partial<IAutocompleteOption>>({
  id: yup.string(),
  groupName: yup.string(),
})
type Owner = {
  followerId?: string
  followerType?: IShipmentIssueFollowerTypes
}
const OwnerSchema = yup.object<Owner>({
  followerId: yup.string(),
  followerType: yup.mixed().oneOf(Object.values(IShipmentIssueFollowerTypes)),
})
const OopShipmentIssueSchema = {
  shipmentIssueTypeId: yup.string().notRequired(),
  state: yup.mixed().oneOf(Object.values(IShipmentIssueStates)).notRequired(),
  owner: OwnerSchema.notRequired(),
  subscribers: yup.array().of(SubscribersSchema).notRequired(),
  summary: yup.string().notRequired(),
}

export const AccessorialSchema = yup.object({
  shipperRejectionReason: yup.string().nullable(),
  shipperApprovalState: yup.string().nullable(),
  /**
   * @example 'Bridge Toll'
   */
  name: yup.string().required(REQUIRED_MESSAGE),
  /**
   * @example 100
   */
  shipperCharge: yup.number().nullable(),
  /**
   * @example 100
   */
  amount: yup.number().nullable(),
  /**
   * @default false
   */
  relayPaymentsId: yup.string().nullable(),
  autoSendSms: yup.boolean().default(false),
  /**
   * @default false
   */
  isCustomerRequest: yup.boolean().default(false),
  /**
   * @default false
   */
  isOutOfPocket: yup.boolean().default(false),
  /**
   * @example 'ce3f68cf-30fe-488c-83e2-f8760438eb22'
   */
  shipperFacilityUuid: yup.string().when('paymentType', {
    is: IPaymentTypes.relay,
    then: yup.string().required('Must select a shipper facility when adding relay payments'),
    otherwise: yup.string().notRequired(),
  }),
  /**
   * @example '2022-10-21'
   */
  startAtDate: yup
    .string()
    .nullable()
    .when('name', {
      is: IShipmentAccessorialNames.trailer_detention_fee,
      then: yup.string().required(REQUIRED_MESSAGE),
      otherwise: yup.string().nullable(),
    }),
  /**
   * @example '12:00'
   */
  startAtTime: yup
    .string()
    .nullable()
    .when('name', {
      is: IShipmentAccessorialNames.trailer_detention_fee,
      then: yup.string().required(REQUIRED_MESSAGE_SHORT),
      otherwise: yup.string().nullable(),
    }),
  /**
   * @example '2022-10-21'
   */
  endAtDate: yup
    .string()
    .nullable()
    .when('name', {
      is: IShipmentAccessorialNames.trailer_detention_fee,
      then: yup.string().required(REQUIRED_MESSAGE),
      otherwise: yup.string().nullable(),
    }),
  /**
   * @example '12:00'
   */
  endAtTime: yup
    .string()
    .nullable()
    .when('name', {
      is: IShipmentAccessorialNames.trailer_detention_fee,
      then: yup.string().required(REQUIRED_MESSAGE_SHORT),
      otherwise: yup.string().nullable(),
    }),
  trailerControlRecord: yup.string(),
  escortServiceName: yup.string(),
  /**
   * @example '1234'
   */
  taxId: yup.string(),
  /**
   * @default false
   */
  withComcheck: yup.boolean().default(false),
  description: yup.string(),
  internalNotesDescription: yup.string().notRequired(),
  lumperServiceName: yup.string(),
  oopReason: yup
    .string()
    .nullable()
    .when('isOutOfPocket', {
      is: true,
      then: yup.string().required(REQUIRED_MESSAGE),
      otherwise: yup.string().nullable().notRequired(),
    }),
  oopOwnerId: yup.string().nullable().notRequired(),
  oopOwnerType: yup.mixed().oneOf(Object.values(IShipmentIssueFollowerTypes)).notRequired(),
  ...OopShipmentIssueSchema,
  comcheckId: yup.string(),
  document: yup
    .object({
      document: yup.string().required(REQUIRED_MESSAGE),
      documentFileName: yup.string().required(REQUIRED_MESSAGE),
    })
    .notRequired()
    .default(undefined),
  /**
   * 'Relay Payment'
   * @default null
   * @example IPaymentTypes.reimbursement
   */
  paymentType: yup
    .mixed<IPaymentTypes | null>()
    .oneOf([...$enum(IPaymentTypes).getValues(), null])
    .nullable()
    .default(null),
  /**
   * 'Accessorial Request'
   * @default null
   * @example 200
   */
  constraintAmount: yup.number().nullable().default(null),
  /**
   * 'Actual Amount'
   * @default null
   * @example 200
   */
  amountUsed: yup.number().nullable().default(null),
  deleteAssetCheck: yup.boolean().notRequired().default(false),
  /**
   * 'Code Status'
   * @example ICodeStatuses.active
   */
  codeStatus: yup.string().notRequired().default(undefined),
  /**
   * 'Code Type'
   * @example ICodeTypes.digital_card
   */
  codeType: yup
    .mixed<ICodeTypes | null>()
    .oneOf([...$enum(ICodeTypes).getValues(), null])
    .notRequired()
    .default(undefined),
})

export type TAccessorialSchema = yup.InferType<typeof AccessorialSchema>
